import { useMemo } from 'react';

import { useBranches } from './branches';
import { useDepartments } from './departments';
import { useProjects } from './projects';

/**
 * Returns a `Map<branchUuid, nameTh>` for the active company.
 * `activeOnly: false` is used so we can resolve UUIDs that point to
 * inactive branches (display history of deactivated records).
 */
export function useBranchNameMap(companyId: string | undefined): {
  isPending: boolean;
  map: Map<string, string>;
} {
  const { data, isPending } = useBranches(companyId, false);
  const map = useMemo(() => {
    const m = new Map<string, string>();
    if (data) for (const b of data) if (b.id) m.set(b.id, b.nameTh);
    return m;
  }, [data]);
  return { isPending, map };
}

export function useDepartmentNameMap(companyId: string | undefined): {
  isPending: boolean;
  map: Map<string, string>;
} {
  const { data, isPending } = useDepartments(companyId, false);
  const map = useMemo(() => {
    const m = new Map<string, string>();
    if (data) for (const d of data) if (d.id) m.set(d.id, d.nameTh);
    return m;
  }, [data]);
  return { isPending, map };
}

export function useProjectNameMap(companyId: string | undefined): {
  isPending: boolean;
  map: Map<string, string>;
} {
  const { data, isPending } = useProjects(companyId, false);
  const map = useMemo(() => {
    const m = new Map<string, string>();
    if (data) for (const p of data) if (p.id) m.set(p.id, p.nameTh);
    return m;
  }, [data]);
  return { isPending, map };
}

/**
 * Convenience: resolves a UUID into its display name, or returns the UUID itself
 * (truncated to 8 chars + ellipsis) if it can't be resolved (lookup still loading,
 * or FK points at a row outside the active company's master list).
 */
export function resolveName(map: Map<string, string>, id: string | null | undefined): string {
  if (!id) return '—';
  const name = map.get(id);
  if (name) return name;
  return id.slice(0, 8) + '…';
}
